import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { State } from "../base/types";
import {
  AssignWalletResponse,
  Balance as BaseBalance,
  SaveFrozenBalance,
  TransferMoneyForm,
  UsdtPrice,
  getAssignWallet as getAssignWalletApi,
  getBalances as getBalancesApi,
  getSaveFrozenBalances as getSaveFrozenBalancesApi,
  getUsdtPrice as getUsdtPriceApi,
  transferMoney as transferMoneyApi,
} from "./api";

export type BalanceState = State<{
  usdtPrice: number | null;
  payIn: number | null;
  payOut: number | null;
  frozen: number | null;
  trust: number | null;
  save: number | null;
  loadingSave: boolean;
  loadingUsdtPrice: boolean;
  errorSave: string | null;
  errorUsdtPrice: string | null;
}>;

export const getBalances = createAsyncThunk<BaseBalance, string>(
  "balance/getBalances",
  async (userId, { rejectWithValue }) => {
    try {
      return await getBalancesApi(userId);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getSaveFrozenBalance = createAsyncThunk<SaveFrozenBalance, string>(
  "balance/getSaveFrozenBalances",
  async (userId, { rejectWithValue }) => {
    try {
      return await getSaveFrozenBalancesApi(userId);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getAssignWallet = createAsyncThunk<AssignWalletResponse, string>(
  "balance/getAssignWallet",
  async (userId, { rejectWithValue }) => {
    try {
      return await getAssignWalletApi(userId);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const transferMoney = createAsyncThunk<string, TransferMoneyForm>(
  "balance/transferMoney",
  async (data, { rejectWithValue }) => {
    try {
      return await transferMoneyApi(data);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getUsdtPrice = createAsyncThunk<UsdtPrice>(
  "balance/getUsdtPrice",
  async (_, { rejectWithValue }) => {
    try {
      return await getUsdtPriceApi();
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const initialState: BalanceState = {
  usdtPrice: null,
  payIn: null,
  payOut: null,
  frozen: null,
  trust: null,
  save: null,

  loading: false,
  loadingSave: false,
  loadingUsdtPrice: false,

  error: null,
  errorSave: null,
  errorUsdtPrice: null,
};

const balanceSlice = createSlice({
  name: "balance",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getBalances.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSaveFrozenBalance.pending, (state) => {
        state.loadingSave = true;
        state.errorSave = null;
      })
      .addCase(getUsdtPrice.pending, (state) => {
        state.loadingUsdtPrice = true;
        state.errorUsdtPrice = null;
      })
      .addCase(getBalances.fulfilled, (state, action) => {
        state.loading = false;
        state.payIn = action.payload.pay_in;
        state.payOut = action.payload.pay_out;
        state.frozen = action.payload.frozen_balance;
        state.trust = action.payload.trust_balance;
      })
      .addCase(getSaveFrozenBalance.fulfilled, (state, action) => {
        state.loadingSave = false;
        state.save = action.payload.save_balance;
        state.frozen = action.payload.frozen_balance;
      })
      .addCase(getUsdtPrice.fulfilled, (state, action) => {
        state.loadingUsdtPrice = false;
        state.usdtPrice = action.payload.currentPrice;
      })
      .addCase(transferMoney.fulfilled, (state, action) => {
        const amount = action.meta.arg.amount;

        if (action.meta.arg.operation === "pay_in" && state.payIn) {
          state.payIn -= amount;
        } else if (action.meta.arg.operation === "pay_out" && state.payOut) {
          state.payOut -= amount;
        }

        state.trust = (state.trust ?? 0) + amount;
      })
      .addCase(getBalances.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getSaveFrozenBalance.rejected, (state, action) => {
        state.loadingSave = false;
        state.errorSave = action.payload as string;
      })
      .addCase(getUsdtPrice.rejected, (state, action) => {
        state.loadingUsdtPrice = false;
        state.errorUsdtPrice = action.payload as string;
      });
  },
});

export default balanceSlice.reducer;
